import CryptoJS from "crypto-js";

const VAULT_VERSION_PREFIX = "v2:";
const PBKDF2_ITERATIONS = 310000;
const LEGACY_PBKDF2_ITERATIONS = 10000;
const AES_KEY_BITS = 256;
const GCM_IV_BYTES = 12;

const encodeText = (value) => new TextEncoder().encode(value);
const decodeText = (value) => new TextDecoder().decode(value);

const bytesToBase64 = (bytes) => {
    let binary = "";
    bytes.forEach((b) => {
        binary += String.fromCharCode(b);
    });
    return btoa(binary);
};

const base64ToBytes = (base64) => {
    const cleaned = String(base64).replace(/\s+/g, "");
    const binary = atob(cleaned);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
};

/** Strip BOM / surrounding whitespace from stored or downloaded ciphertext. */
export const normalizeCiphertext = (ciphertext) => {
    if (ciphertext == null) return "";
    return String(ciphertext).replace(/^\uFEFF/, "").trim();
};

const formatError = (error) => {
    if (!error) return "Unknown error";
    if (typeof error === "string") return error;
    const name = error.name || "";
    const message = error.message || "";
    if (name && message) return `${name}: ${message}`;
    return name || message || Object.prototype.toString.call(error);
};

/** Wrong key / corrupt auth tag — expected, not a bug to surface in extension Errors. */
const isExpectedDecryptFailure = (error) =>
    error?.name === "OperationError" || error?.name === "InvalidCharacterError";

export const createSalt = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

export const isV2Ciphertext = (ciphertext) => {
    const normalized = normalizeCiphertext(ciphertext);
    return normalized.startsWith(VAULT_VERSION_PREFIX);
};

/** @deprecated kept for detecting legacy/plaintext import payloads */
export const isEncrypted = (data) => {
    if (!data || typeof data !== "string") return false;
    const normalized = normalizeCiphertext(data);
    if (isV2Ciphertext(normalized)) return true;
    try {
        JSON.parse(normalized);
        return false;
    } catch {
        return true;
    }
};

const deriveLegacyPassphrase = (password, salt) =>
    CryptoJS.PBKDF2(password, salt, {
        keySize: AES_KEY_BITS / 32,
        iterations: LEGACY_PBKDF2_ITERATIONS,
        hasher: CryptoJS.algo.SHA512,
    }).toString();

export const deriveKey = async (password, salt) => {
    const keyMaterial = await crypto.subtle.importKey(
        "raw",
        encodeText(password),
        "PBKDF2",
        false,
        ["deriveKey"]
    );

    return crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt: encodeText(salt),
            iterations: PBKDF2_ITERATIONS,
            hash: "SHA-256",
        },
        keyMaterial,
        { name: "AES-GCM", length: AES_KEY_BITS },
        false,
        ["encrypt", "decrypt"]
    );
};

const encryptWithKey = async (data, cryptoKey) => {
    const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_BYTES));
    const plaintext = encodeText(JSON.stringify(data));
    const ciphertext = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        cryptoKey,
        plaintext
    );

    const payload = new Uint8Array(iv.length + ciphertext.byteLength);
    payload.set(iv, 0);
    payload.set(new Uint8Array(ciphertext), iv.length);

    return VAULT_VERSION_PREFIX + bytesToBase64(payload);
};

const decryptV2 = async (ciphertext, cryptoKey) => {
    const raw = base64ToBytes(ciphertext.slice(VAULT_VERSION_PREFIX.length));
    if (raw.length <= GCM_IV_BYTES) {
        return null;
    }

    // Copy views — some browsers reject sliced TypedArray buffers for AES-GCM.
    const iv = new Uint8Array(raw.subarray(0, GCM_IV_BYTES));
    const data = new Uint8Array(raw.subarray(GCM_IV_BYTES));

    const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv },
        cryptoKey,
        data
    );

    return JSON.parse(decodeText(decrypted));
};

const decryptLegacy = (ciphertext, passphrase) => {
    try {
        const bytes = CryptoJS.AES.decrypt(ciphertext, passphrase);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        if (!decrypted) return null;
        return JSON.parse(decrypted);
    } catch {
        return null;
    }
};

/**
 * Session helpers bound to the unlocked vault.
 * decrypt() understands both v2 AES-GCM and legacy CryptoJS ciphertext
 * so sync can migrate remote blobs.
 */
export const createVaultSession = async (password, salt) => {
    const cryptoKey = await deriveKey(password, salt);
    const legacyPassphrase = deriveLegacyPassphrase(password, salt);

    const encrypt = async (data) => {
        try {
            return await encryptWithKey(data, cryptoKey);
        } catch (error) {
            console.error("Encryption error:", formatError(error));
            return null;
        }
    };

    const decrypt = async (ciphertext) => {
        const normalized = normalizeCiphertext(ciphertext);
        if (!normalized) return null;

        try {
            if (isV2Ciphertext(normalized)) {
                return await decryptV2(normalized, cryptoKey);
            }
            return decryptLegacy(normalized, legacyPassphrase);
        } catch (error) {
            // OperationError = wrong password/key; do not console.error
            // (Chrome surfaces console.error on the extension Errors page).
            if (!isExpectedDecryptFailure(error)) {
                console.error("Decryption error:", formatError(error));
            }
            return null;
        }
    };

    return { cryptoKey, encrypt, decrypt };
};

/** @deprecated prefer vaultSession.encrypt */
export const encryptData = async (data, encryptionKey) => {
    if (!encryptionKey) {
        console.error("Encryption key not initialized");
        return null;
    }

    if (typeof encryptionKey.encrypt === "function") {
        return encryptionKey.encrypt(data);
    }

    try {
        return await encryptWithKey(data, encryptionKey);
    } catch (error) {
        console.error("Encryption error:", formatError(error));
        return null;
    }
};

/** @deprecated prefer vaultSession.decrypt */
export const decryptData = async (ciphertext, encryptionKey) => {
    if (!encryptionKey || !ciphertext) return null;

    if (typeof encryptionKey.decrypt === "function") {
        return encryptionKey.decrypt(ciphertext);
    }

    try {
        const normalized = normalizeCiphertext(ciphertext);
        if (isV2Ciphertext(normalized)) {
            return await decryptV2(normalized, encryptionKey);
        }
        return null;
    } catch (e) {
        if (!isExpectedDecryptFailure(e)) {
            console.error("Decryption error:", formatError(e));
        }
        return null;
    }
};

/**
 * Unlock vault: derive session, decrypt v2 or migrate legacy CryptoJS ciphertext.
 * @returns {{ session: object, data: any|null, migrated: boolean }|null}
 */
export const unlockVault = async (password, salt, encryptedData) => {
    const session = await createVaultSession(password, salt);
    const normalized = normalizeCiphertext(encryptedData);

    if (!normalized) {
        return { session, data: null, migrated: false };
    }

    const data = await session.decrypt(normalized);
    if (data === null) {
        return null;
    }

    const migrated = !isV2Ciphertext(normalized);
    return { session, data, migrated };
};

export const mergePasswords = (local, remote) => {
    const mergedMap = new Map();
    const allEntries = [...local, ...remote];

    allEntries.forEach((item) => {
        const existing = mergedMap.get(item.key);
        const currentTime = existing ? getEffectiveTime(existing) : -Infinity;
        const newTime = getEffectiveTime(item);

        if (newTime > currentTime) {
            mergedMap.set(item.key, item);
        } else if (newTime === currentTime) {
            if (item.deletedAt) mergedMap.set(item.key, item);
        }
    });

    return Array.from(mergedMap.values()).filter((item) => {
        return !item.deletedAt || new Date(item.updatedAt) > new Date(item.deletedAt);
    });
};

const getEffectiveTime = (item) => {
    return Math.max(
        new Date(item.updatedAt).getTime(),
        item.deletedAt ? new Date(item.deletedAt).getTime() : 0
    );
};

export const CRYPTO_PARAMS = {
    versionPrefix: VAULT_VERSION_PREFIX,
    pbkdf2Iterations: PBKDF2_ITERATIONS,
};
